import React, { useMemo, useState, useEffect } from 'react';
import { Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import moment from 'moment';
import { useStore } from '../../../../store';
import { useMapContext } from '../context/map-context';

const TIME_FORMATS: moment.MomentFormatSpecification = [
  moment.ISO_8601,
  'YYYY-MM-DD HH:mm:ss.SSS z',
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY-MM-DD-HH:mm:ss-z',
  'YYYY-MM-DD-HH:mm:ss',
  'YYYY-MM-DD HH:mm:ss',
];

const ARROW_ICON_PX = 20;
const createArrowIcon = (angleDeg: number) => L.divIcon({
  html: `<div style="transform:rotate(${angleDeg}deg);width:${ARROW_ICON_PX}px;height:${ARROW_ICON_PX}px;display:flex;align-items:center;justify-content:center;">
    <svg width="${ARROW_ICON_PX}" height="${ARROW_ICON_PX}" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
      <polygon points="6,0 11,12 6,8 1,12" fill="#5D30F0"/>
    </svg>
  </div>`,
  iconSize: [ARROW_ICON_PX, ARROW_ICON_PX],
  iconAnchor: [ARROW_ICON_PX / 2, ARROW_ICON_PX / 2],
  className: '',
});

export const TrailPolyline = (): JSX.Element | null => {
  const { mapOrigin, mapReport } = useStore();
  const { imageAngle, rangePerDegree, configurationValues } = useMapContext();
  const map = useMap();

  const [mapBearing, setMapBearing] = useState<number>(() => {
    const m = map as unknown as { getBearing?: () => number };
    return typeof m.getBearing === 'function' ? m.getBearing() : 0;
  });

  useEffect(() => {
    const m = map as unknown as { getBearing?: () => number };
    const handleRotate = () => {
      setMapBearing(typeof m.getBearing === 'function' ? m.getBearing() : 0);
    };
    map.on('rotate', handleRotate);
    handleRotate();
    return () => {
      map.off('rotate', handleRotate);
    };
  }, [map]);

  const { positions, arrows } = useMemo(() => {
    if (!configurationValues.visibility.snailTrail
      || !mapReport.data?.recordsMap || !mapOrigin.data.latitude
      || !mapOrigin.data.longitude || !rangePerDegree) {
      return { positions: [], arrows: [] };
    }

    const toLatLng = (X: number, Y: number): [number, number] | null => {
      if (!Number.isFinite(X) || !Number.isFinite(Y)) return null;
      const latitude = Number(mapOrigin.data.latitude) + Y / rangePerDegree.lat;
      const longitude = Number(mapOrigin.data.longitude) + X / rangePerDegree.lon;
      const angleRad = (imageAngle * Math.PI) / 180;
      const pointProjected = map.project(L.latLng([latitude, longitude]));
      const centerProjected = map.project(
        L.latLng([Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)]),
      );
      const deltaX = pointProjected.x - centerProjected.x;
      const deltaY = pointProjected.y - centerProjected.y;
      const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
      const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);
      const rotatedPointProjected = L.point(
        centerProjected.x + rotatedX,
        centerProjected.y + rotatedY,
      );
      const rotatedLatLng = map.unproject(rotatedPointProjected);
      return [rotatedLatLng.lat, rotatedLatLng.lng];
    };

    const records = Object.values(mapReport.data.recordsMap);
    const timestamps = records.map((r) => {
      if (!r.Time) return null;
      const parsed = moment(r.Time, TIME_FORMATS);
      return parsed.isValid() ? parsed.valueOf() : null;
    });
    const orderedRecords = timestamps.every((t) => t !== null)
      ? records
        .map((r, i) => ({ r, t: timestamps[i] as number }))
        .sort((a, b) => a.t - b.t)
        .map((entry) => entry.r)
      : records;

    const trailPoints = orderedRecords
      .map((r) => toLatLng(Number(r.X || r.x), Number(r.Y || r.y)))
      .filter((p): p is [number, number] => p !== null);

    if (trailPoints.length < 2) return { positions: trailPoints, arrows: [] };

    const uniquePoints = trailPoints.filter((p, i) => {
      if (i === 0) return true;
      return p[0] !== trailPoints[i - 1][0] || p[1] !== trailPoints[i - 1][1];
    });

    const quantize = (n: number) => n.toFixed(6);
    type SegmentGroup = {
      pA: [number, number];
      pB: [number, number];
      hasAtoB: boolean;
      hasBtoA: boolean;
    };
    const segmentGroups = new Map<string, SegmentGroup>();

    for (let i = 0; i < uniquePoints.length - 1; i += 1) {
      const currentPoint = uniquePoints[i];
      const nextPoint = uniquePoints[i + 1];
      const currentKey = `${quantize(currentPoint[0])},${quantize(currentPoint[1])}`;
      const nextKey = `${quantize(nextPoint[0])},${quantize(nextPoint[1])}`;
      if (currentKey !== nextKey) {
        const isForward = currentKey < nextKey;
        const canonicalKey = isForward ? `${currentKey}|${nextKey}` : `${nextKey}|${currentKey}`;
        const existingGroup = segmentGroups.get(canonicalKey);
        if (existingGroup) {
          if (isForward) existingGroup.hasAtoB = true; else existingGroup.hasBtoA = true;
        } else {
          segmentGroups.set(canonicalKey, {
            pA: isForward ? currentPoint : nextPoint,
            pB: isForward ? nextPoint : currentPoint,
            hasAtoB: isForward,
            hasBtoA: !isForward,
          });
        }
      }
    }

    const ARROW_OFFSET_FROM_START = 2 / 3;
    const arrowList: { position: [number, number]; angle: number; key: string }[] = [];

    segmentGroups.forEach((group, canonicalKey) => {
      const pAProjected = map.project(L.latLng(group.pA));
      const pBProjected = map.project(L.latLng(group.pB));
      const deltaX = pBProjected.x - pAProjected.x;
      const deltaY = pBProjected.y - pAProjected.y;
      if (deltaX === 0 && deltaY === 0) return;

      const aToBAngle = (
        (Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90 + mapBearing + 360
      ) % 360;
      const bToAAngle = (aToBAngle + 180) % 360;

      const pushArrow = (t: number, angle: number, keySuffix: string) => {
        const arrowLatLng = map.unproject(L.point(
          pAProjected.x + deltaX * t,
          pAProjected.y + deltaY * t,
        ));
        arrowList.push({
          position: [arrowLatLng.lat, arrowLatLng.lng],
          angle,
          key: `${canonicalKey}-${keySuffix}`,
        });
      };

      if (group.hasAtoB) pushArrow(ARROW_OFFSET_FROM_START, aToBAngle, 'ab');
      if (group.hasBtoA) pushArrow(1 - ARROW_OFFSET_FROM_START, bToAAngle, 'ba');
    });

    return { positions: trailPoints, arrows: arrowList };
  }, [
    mapReport.data.recordsMap, mapOrigin.data, imageAngle, rangePerDegree,
    configurationValues.visibility.snailTrail, mapBearing,
  ]);

  if (!positions.length) return null;

  return (
    <>
      <Polyline
        positions={positions}
        pathOptions={{ color: '#5D30F0', weight: 2, opacity: 0.8 }}
      />
      {arrows.map(({ position, angle, key }) => (
        <Marker
          key={key}
          position={position}
          icon={createArrowIcon(angle)}
        />
      ))}
    </>
  );
};
